import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Check, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/**
 * EditProfileModal - Modal for editing user profile
 * @param {boolean} isOpen - Whether modal is open
 * @param {function} onClose - Close callback
 * @param {object} user - Current auth user
 * @param {object} profile - Current profile data
 * @param {function} onProfileUpdate - Callback when profile is updated
 */
export default function EditProfileModal({ isOpen, onClose, user, profile, onProfileUpdate }) {
  const [username, setUsername] = useState(profile?.username || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [usernameError, setUsernameError] = useState(null);
  const fileInputRef = useRef(null);

  const validateUsername = (value) => {
    if (value.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      return false;
    }
    if (value.length > 30) {
      setUsernameError('Username must be less than 30 characters');
      return false;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      setUsernameError('Only letters, numbers, and underscores');
      return false;
    }
    setUsernameError(null);
    return true;
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (value) {
      validateUsername(value);
    } else {
      setUsernameError(null);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const fileExt = file.name.split('.').pop();
      const filePath = `${user.id}/${Date.now()}.${fileExt}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setAvatarUrl(urlData.publicUrl);
    } catch (err) {
      console.error('Avatar upload error:', err);
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!validateUsername(username)) return;

    setSaving(true);
    setError(null);

    try {
      const { data, error: updateError } = await supabase
        .from('profiles')
        .update({
          username: username.toLowerCase(),
          avatar_url: avatarUrl,
        })
        .eq('id', user.id)
        .select()
        .single();

      if (updateError) {
        if (updateError.code === '23505') {
          setUsernameError('This username is already taken');
          setSaving(false);
          return;
        }
        throw updateError;
      }

      // Notify parent of update
      if (onProfileUpdate) {
        onProfileUpdate(data);
      }
      onClose();
    } catch (err) {
      console.error('Profile update error:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-lg bg-gjyl-dark rounded-t-3xl overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <h2 className="text-xl font-bold text-white">Edit Profile</h2>
              <motion.button
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white"
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <X size={20} />
              </motion.button>
            </div>

            {/* Content */}
            <div className="px-6 py-6 space-y-6">
              {/* Avatar */}
              <div className="flex flex-col items-center">
                <div className="relative">
                  <motion.div
                    className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500"
                    whileHover={{ scale: 1.05 }}
                  >
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white text-3xl font-bold">
                        {(username || user?.email || 'U').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </motion.div>

                  {/* Upload button */}
                  <motion.button
                    className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-gjyl-accent flex items-center justify-center text-white shadow-lg"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {uploading ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Camera size={16} />
                    )}
                  </motion.button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                </div>
                <p className="text-gray-400 text-xs mt-2">Tap to change photo</p>
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={handleUsernameChange}
                  className={`w-full px-4 py-3 bg-zinc-900 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 transition-all ${
                    usernameError
                      ? 'border-red-500 focus:ring-red-500/50'
                      : 'border-white/10 focus:ring-purple-500/50'
                  }`}
                  placeholder="Enter username"
                />
                {usernameError && (
                  <p className="mt-1 text-xs text-red-400">{usernameError}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  3-30 characters, letters, numbers, underscores
                </p>
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-xl text-red-300 text-sm">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 pb-8 border-t border-white/10">
              <motion.button
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50"
                onClick={handleSave}
                disabled={saving || uploading || !!usernameError || !username}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    <span>Save Changes</span>
                  </>
                )}
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
